import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Truck, FileText, PackageOpen } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, Loading, EmptyState } from "../../components/common";
import { ShareDealButton } from "../../components/ShareDealButton";
import { DealPdfShareDialog } from "../../components/DealPdfShareDialog";
import { api, mediaUrl } from "../../lib/api";
import { pkr, commissionText, commissionPerUnit, AVAILABILITY } from "../../lib/format";

const isVideoFile = (p) => /\.(mp4|webm|mov)$/i.test(p || "");
const youtubeEmbed = (u) => {
  const m = (u || "").match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

export default function DealDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [img, setImg] = useState("");
  useEffect(() => { api.get(`/deals/${id}`).then((r) => { setD(r.data); setImg(r.data.main_image); }).catch(() => setErr(true)); }, [id]);
  if (err) return <EmptyState icon={PackageOpen} title="Deal not available" message="This deal may have been unpublished." action={<Button asChild><Link to="/app/deals">Back to deals</Link></Button>} />;
  if (!d) return <Loading />;
  const gallery = [d.main_image, ...(d.images || [])].filter(Boolean);
  const yt = youtubeEmbed(d.video_url);
  return (
    <div className="space-y-6">
      <Link to="/app/deals" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"><ArrowLeft className="h-4 w-4" />All deals</Link>
      <div className="grid gap-8 lg:grid-cols-5">
        <div className="lg:col-span-3 space-y-3">
          <div className="aspect-[16/10] overflow-hidden rounded-xl bg-slate-100">{img ? <img src={mediaUrl(img)} alt={d.name} className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-slate-400"><PackageOpen className="h-12 w-12" /></div>}</div>
          {gallery.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">{gallery.map((g) => (
              <button key={g} onClick={() => setImg(g)} className={`h-16 w-24 shrink-0 overflow-hidden rounded-lg border-2 ${img === g ? "border-emerald-500" : "border-transparent"}`}><img src={mediaUrl(g)} alt="" className="h-full w-full object-cover" /></button>
            ))}</div>
          )}
          {d.video_file && isVideoFile(d.video_file) && <video controls src={mediaUrl(d.video_file)} className="w-full rounded-xl" data-testid="deal-video" />}
          {yt && <iframe title="Promotional video" src={yt} className="aspect-video w-full rounded-xl" allowFullScreen />}
          {d.video_url && !yt && <a href={d.video_url} target="_blank" rel="noreferrer" className="text-sm text-emerald-700 underline">Watch promotional video</a>}
        </div>
        <div className="lg:col-span-2 space-y-5">
          <div>
            <p className="font-mono text-xs text-slate-400">{d.ref} · {d.category}</p>
            <h1 className="mt-1 font-heading text-2xl sm:text-3xl font-bold" data-testid="deal-title">{d.name}</h1>
            <p className="mt-2 text-sm font-medium text-slate-600">{AVAILABILITY[d.availability]}{d.stock_qty != null && ` · ${d.stock_qty} left`}</p>
          </div>
          <Card className="divide-y">
            <div className="flex items-center justify-between p-4"><span className="text-slate-600">Fixed customer price</span><span className="font-mono text-xl font-bold" data-testid="deal-price">{pkr(d.price)}</span></div>
            <div className="flex items-center justify-between bg-emerald-50 p-4"><span className="text-emerald-800">Your commission</span>
              <span className="text-right"><span className="block font-mono text-xl font-bold text-emerald-700" data-testid="deal-commission">{commissionText(d)}</span>
                {d.commission_type === "percent" && <span className="text-xs text-emerald-700">≈ {pkr(commissionPerUnit(d))} per unit</span>}</span></div>
          </Card>
          <Button asChild className="h-12 w-full text-base" disabled={d.availability === "out_of_stock"}><Link to={`/app/book?deal=${d.id}`} data-testid="deal-detail-book-btn">Book an order for this deal</Link></Button>
          <ShareDealButton d={d} full className="h-12 w-full text-base" />
          <DealPdfShareDialog d={d} trigger={<Button variant="outline" className="h-12 w-full text-base" data-testid="deal-detail-pdf-btn"><FileText className="mr-2 h-5 w-5" />Posters, PDF &amp; social sharing</Button>} />
          <p className="text-xs text-slate-500">Prices are fixed. Please do not offer discounts or different prices to customers.</p>
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2"><h2 className="font-heading text-lg font-semibold">Description</h2><p className="mt-2 whitespace-pre-line text-slate-700">{d.description || "—"}</p></Card>
        <div className="space-y-6">
          <Card className="p-5"><h2 className="flex items-center gap-2 font-heading text-lg font-semibold"><Truck className="h-5 w-5" />Delivery / fulfilment</h2><p className="mt-2 whitespace-pre-line text-sm text-slate-700">{d.delivery_info || "—"}</p></Card>
          <Card className="p-5"><h2 className="flex items-center gap-2 font-heading text-lg font-semibold"><FileText className="h-5 w-5" />Terms</h2><p className="mt-2 whitespace-pre-line text-sm text-slate-700">{d.terms || "—"}</p></Card>
        </div>
      </div>
    </div>
  );
}
